import { biomeForLevel, type BiomeId } from '../../content/biomes';
import Phaser from 'phaser';
import { allowedMowers, type LevelDef } from '../../content/levelSchema';
import { COSMETICS, CRITTER_TAG_COSMETIC } from '../../content/cosmetics';
import { parGoalTicks, starGoals } from '../starGoals';
import { needsFullGame } from '../../meta/unlock';
import { simTool, TOOL_BY_ID } from '../../content/tools';
import { MOWER_DEFS, MOWERS, type MowerId } from '../../content/mowers';
import {
  endlessCarry,
  endlessRowsClimbed,
  endlessStage,
  endlessStagePassed,
  endlessStageScore,
  type EndlessMode,
} from '../../gen/modes';
import { GreedyPlanner } from '../../gen/solver/greedyPlanner';
import {
  applySettlement,
  campaignProgress,
  mowerOpen,
  dailyReward,
  boostStock,
  effectiveMower,
  equippedPaint,
  equippedStripes,
  spendBoost,
  bankLines,
  endlessLawnLines,
  endlessPayout,
  linesTotal,
  loadout,
  mowerMastered,
  toolLevel,
  upgradedTool,
  type CoinLine,
  settleAttempt,
  type AttemptResult,
  continueCost,
  payForContinue,
  rareFindEscapes,
} from '../../meta/economy';
import { TICK_RATE } from '../../sim/constants';
import type { SimEvent } from '../../sim/events';
import type { Intent, SteerIntent } from '../../sim/mower';
import { Simulation } from '../../sim/simulation';
import { haptics } from '../../platform/haptics';
import { gameAudio } from '../audio/audio';
import { exposeDebug } from '../debug';
import { FixedStepper } from '../fixedStep';
import { InputController } from '../input/InputController';
import { TouchDebug } from '../input/TouchDebug';
import { HUD_HEIGHT, TILE_SIZE, TOOLBAR_HEIGHT } from '../layout';
import { LevelView } from '../render/LevelView';
import {
  dailyBestTicks,
  endlessBest,
  newRunSeed,
  saves,
  submitDailyTicks,
  submitEndlessScore,
  todayKey,
} from '../records';
import { loadControlScheme, type ControlScheme } from '../settings';
import { UI } from '../ui/text';
import { reducedMotion } from '../ui/motion';
import { formatClock, formatTime } from '../ui/widgets';
import type { HudScene, StartCard } from './HudScene';
import { storePreview } from '../devAccess';
import type { PracticeItem } from '../../content/practice';
import { loadoutFor, postedRun } from '../../meta/online/outbox';
import { postedLawn, type PostedLawn, type PostedRun } from '../../meta/online/run';
import { answerChallenge, boardResultFor, onlineOn, queueRun, sendChallenge } from '../online';
import { rankText } from '../rankText';
import { shareLink } from '../../platform/share';
import { GhostRun } from '../render/GhostRun';
import { MOWER_DEPTH } from '../render/LevelView';
import { nameText } from '../../content/names';
import type { PlayerName } from '../../meta/online/schema';
import type { BoardsData } from './BoardsScene';
import {
  communityBoard,
  FEATURED,
  goalsBoard,
  isSoccerLawn,
  lawnBoard,
} from '../../meta/online/boards';
import type { ResultsModel } from './ResultsScene';
import { onAppHidden } from '../../platform/native';
import { syncDailyReminders } from '../reminders';
import { wipeTo } from './WipeScene';
import { engineInUse, judgeScores, tagCritter } from '../../meta/economy';
import { lawnWithYard, secretYards } from '../secretYards';
import type { SecretYard } from '../../gen/secretYards';
import type { RevealData } from './RevealScene';
import { REG_GARAGE_OPEN, REG_GARAGE_RESUME } from './GarageScene';
import { campaignReveals, takeMilestones, type Reveal } from '../../meta/reveals';
import { hasTallGrass, tallGrassNote } from '../../content/tiles';
import { StormPilot } from '../../gen/solver/stormBot';
import { ArtPilot } from '../../gen/solver/artBot';
import { HerdPilot } from '../../gen/solver/herdBot';
import { SoccerPilot } from '../../gen/solver/soccerBot';
import { artLegs, artRows } from '../../gen/boss';
import { BOOST_IDS, BOOST_TICKS, type BoostId } from '../../sim/boosts';
import { challengeFor } from '../../content/challenges';
import { CAMPAIGN_LEVELS } from '../../content/levels/campaign';
import { lawnWithFinds, rareFindOn, type RareFind } from '../rareFinds';
import { whoText } from '../nicknames';
import { isItemId, type ItemId } from '../../sim/items';
import type { CritterKind } from '../../sim/critters';
import { Tile } from '../../sim/grid';

/** A sequence of lawns played in order ("next lawn" moves along it). */
export interface Playlist {
  /** Campaign lawns pay out and record progress; test lawns don't. */
  kind: 'campaign' | 'test';
  /** Short label for the HUD, e.g. "Lawn" → "Lawn 7". */
  short: string;
  levels: readonly LevelDef[];
  index: number;
  /**
   * Where leaving goes instead of the menu, what that scene is started with, and the button's
   * label (a builder lawn: back to the builder with it, "Edit").
   */
  back?: { scene: string; label: string; data?: object };
  /** A community lawn (docs/ONLINE.md): a clear goes on its board. */
  community?: { id: string; code: string };
  /**
   * The week's featured campaign lawn (docs/ONLINE.md, "Boards"), played from its board whether
   * the player has reached it or not: a clear goes on the featured board (no campaign rewards).
   */
  featured?: { week: string; lawn: number };
}

export interface EndlessRun {
  /** Which endless mode (Endless Lawns if unset). */
  mode?: EndlessMode;
  runSeed: string;
  /**
   * The mower the run's first lawn was built for (absent: Endless Lawns from the menu, built before
   * a mower is picked). Later lawns are built for the mower played. A posted run needs it.
   */
  firstLawnFor?: MowerId;
  /** 1-based lawn number in the run. */
  stage: number;
  score: number;
  /** HP and fuel carried in from the previous lawn. */
  hp?: number;
  fuel?: number;
  /** Coins and bills from the lawns finished so far, paid out when the run ends. */
  bank?: CoinLine[];
  /** The lawns finished so far, as a posted run sends them (online play). */
  lawns?: PostedLawn[];
  /** The autopilot drove at some point in the run: it is never posted. */
  assisted?: boolean;
}

/** "Run bank: 12 coins" (what an endless run has banked so far). */
const bankNote = (bank: readonly CoinLine[]): string => {
  const n = linesTotal(bank);
  return `Run bank: ${n} coin${n === 1 ? '' : 's'}`;
};

export interface LevelSceneData {
  level: LevelDef;
  mowerId: MowerId;
  playlist?: Playlist;
  /** Set when playing today's Daily (the date key). */
  daily?: string;
  endless?: EndlessRun;
  /** A retry: straight to the countdown, without the start card's tap (owner, 2026-10-23). */
  quickStart?: boolean;
  /**
   * A run raced as a ghost (online play; docs/ONLINE.md): the Daily leader's, a challenge's, or a
   * community lawn's best.
   */
  ghost?: {
    run: PostedRun;
    name: PlayerName;
    /** Whose run it is, when known (a challenge's sender): their nickname shows if they have one. */
    player?: string;
    ticks: number;
    kind?: 'leader' | 'challenge' | 'best';
  };
  /** A beat-my-time challenge being answered (its id): a clear is posted as the answer. */
  challenge?: string;
  /**
   * A Garage TRY IT lawn (content/practice.ts): played with just this item, owned or not; boosts
   * are not used up, and nothing is paid, recorded or won.
   */
  practice?: { item: PracticeItem; hint: string };
}

/** Outside the fence: dark and desaturated so it recedes (docs/ART_DIRECTION.md). */
const BACKGROUND = '#2b4a26';
const MOWER_IDS_BY_UNLOCK = (Object.keys(MOWERS) as MowerId[]).sort(
  (a, b) => MOWER_DEFS[a].unlockLevel - MOWER_DEFS[b].unlockLevel,
);
const END_PROMPT_DELAY_MS = 700;
/** A won lawn gets its celebration (stripe reveal, confetti) before the results card. */
const WIN_PROMPT_DELAY_MS = 1300;
type Phase = 'ready' | 'playing' | 'paused' | 'countdown' | 'ending' | 'ended';
/** Each step of the resume countdown (3, 2, 1). */
const COUNTDOWN_STEP_MS = 600;
/**
 * A secret yard's gate gives way: the lawn holds still (the mower at the gate, the clock stopped)
 * while the yard fades into full view (`YardView.open`), then it rolls on in.
 */
const YARD_REVEAL_HOLD_MS = 1000;

/** Plays one level attempt: owns the sim, steps it at a fixed rate, and drives the view. */
export class LevelScene extends Phaser.Scene {
  private sim!: Simulation;
  private view!: LevelView;
  private controls!: InputController;
  private touchDebug: TouchDebug | null = null;
  /** Moles: the grass left when the molehill hint last looked (-1: look again). */
  private moleLeft = -1;
  /** Moles: the molehills, when they are all that is left to mow (they blink). */
  private moleTiles: { x: number; y: number }[] = [];
  private moleMarks: Phaser.GameObjects.Graphics | null = null;
  private planner: GreedyPlanner | null = null;
  private stormPilot: StormPilot | null = null;
  private artPilot: ArtPilot | null = null;
  private herdPilot: HerdPilot | null = null;
  private soccerPilot: SoccerPilot | null = null;
  private readonly stepper = new FixedStepper();
  private scheme: ControlScheme = 'swipe';
  private phase: Phase = 'ready';
  private countdownTimers: Phaser.Time.TimerEvent[] = [];
  /** A turn picked during the resume countdown, sent to the sim when play resumes. */
  private queuedTurn: SteerIntent | null = null;
  /** The gesture that starts the level is not also a steering request. */
  private autopilot = false;
  /** The autopilot drove at some point this attempt: the run is never posted online. */
  private autopilotUsed = false;
  /** The ghost being raced, if any. */
  private ghost: GhostRun | null = null;
  /** This attempt as posted (a cleared Daily), for CHALLENGE A FRIEND. */
  private lastRun: PostedRun | null = null;
  /** Dev time multiplier (`?speed=4`) for faster automated checks. */
  private speed = 1;
  private levelData!: LevelSceneData;
  private toolCount = 0;
  /**
   * A Daily with item boxes (docs/DAILY_ITEMS.md): the bar holds TRIM and TRBO for what the boxes
   * gave, in place of tools and boosts.
   */
  private boxItems = false;
  /** Boosts carried into this lawn (those in stock when it started), in toolbar order. */
  private boostIds: BoostId[] = [];
  private startCard: StartCard = { title: '', goals: [], notes: [], hint: '' };
  /** Continues bought this attempt (campaign lawns: each costs double the last). */
  private continuesUsed = 0;
  /** Reveal cards waiting for the player to leave the results (zone done, unlocks, ...). */
  private reveals: Reveal[] = [];
  /** The look over a cleared lawn, while it plays (a tap skips it). */
  private flight: Phaser.Tweens.Tween | null = null;
  /** The HUD's coin counter while reward coins are flying into it (null: the wallet). */
  private walletShown: number | null = null;
  /** Endless: the run's bank including this lawn once it is over (see `settle`). */
  private runBank: CoinLine[] = [];
  /** Endless: the run has been paid out (it ended, or the player left). */
  private runPaid = false;
  /** The rare find on this campaign lawn (its own or a wandering one), and whether it got away. */
  private rareFind: RareFind | null = null;
  /** The lawn's secret yard (none on most), the escaped find waiting there, and whether it opened. */
  private yard: SecretYard | null = null;
  private yardRare: string | null = null;
  private yardOpened = false;
  /** Ms left of the hold while a secret yard appears (nothing moves). */
  private revealHold = 0;
  /** This start is a retry: straight to the countdown. */
  private quickStart = false;
  private rareGone = false;
  /** For the challenge star: hearts lost, critter bumps and tool uses this attempt. */
  private heartsLost = 0;
  private critterBumps = 0;
  /** The first squirrel or rabbit tagged on this attempt: its card plays after the results. */
  private tagReveal: Reveal | null = null;
  private toolsUsed = 0;
  /** Boosts fired from stock this attempt (the "Use no boosts" challenge). */
  private boostsUsed = 0;
  /** Tiles already counted toward stats when an earlier failure of this attempt settled. */
  private mowedRecorded = 0;

  constructor() {
    super('Level');
  }

  init(given: LevelSceneData): void {
    // A retry's quick start is for this start only (the Garage's way back starts normally).
    const { quickStart, ...data } = given;
    this.quickStart = quickStart === true;
    // Any lawn started: the Garage's way back to the last one is spent (not by a TRY IT lawn,
    // which goes back to the Garage).
    if (!given.practice) this.registry.remove(REG_GARAGE_RESUME);
    // Campaign lawns are played with their rare finds as they are now (gone, or wandered in).
    const list = data.playlist;
    const n = list?.kind === 'campaign' ? list.index + 1 : null;
    const original = n !== null ? CAMPAIGN_LEVELS[n - 1] : undefined;
    this.rareFind = n !== null && !data.daily ? rareFindOn(saves().save, n) : null;
    this.levelData =
      n !== null && original && !data.daily
        ? { ...data, level: lawnWithFinds(original, n, saves().save) }
        : data;
    // And its secret yard, if it hides one, stocked for this visit.
    this.yard = null;
    this.yardRare = null;
    if (n !== null && original && !data.daily) {
      const stocked = lawnWithYard(this.levelData.level, n, saves().save);
      this.levelData = { ...this.levelData, level: stocked.level };
      this.yard = stocked.yard;
      this.yardRare = stocked.rareItem;
    }
    // An endless run's first lawn was built for the mower picked in the menu: rebuild it for the
    // one chosen on the card if that changed (boss lawns are built for the run's mower).
    const run = data.endless;
    if (
      run?.mode &&
      run.mode !== 'lawns' &&
      run.stage === 1 &&
      !allowedMowers(this.levelData.level.mowerRule).includes(data.mowerId)
    ) {
      this.levelData = {
        ...this.levelData,
        level: endlessStage(run.mode, run.runSeed, 1, data.mowerId),
        endless: { ...run, firstLawnFor: data.mowerId },
      };
    }
    this.rareGone = false;
    this.heartsLost = 0;
    this.critterBumps = 0;
    this.tagReveal = null;
    this.toolsUsed = 0;
    this.boostsUsed = 0;
    this.phase = 'ready';
    this.planner = null;
    this.stormPilot = null;
    this.artPilot = null;
    this.herdPilot = null;
    this.soccerPilot = null;
    this.continuesUsed = 0;
    this.lastRun = null;
    this.mowedRecorded = 0;
    this.flight = null;
    this.walletShown = null;
    this.reveals = [];
    this.yardOpened = false;
    this.revealHold = 0;
    this.runBank = this.levelData.endless?.bank ?? [];
    this.runPaid = false;
    // An endless run is kept in the save as it goes, so closing the app mid-run still pays it.
    const open = this.levelData.endless;
    if (open) this.keepRunOpen(open.stage, open.score);
    const params = new URLSearchParams(window.location.search);
    this.autopilot = params.has('autopilot');
    this.autopilotUsed = this.autopilot;
    this.speed = import.meta.env.DEV
      ? Math.min(8, Math.max(1, Number(params.get('speed')) || 1))
      : 1;
    this.stepper.reset();
  }

  create(): void {
    // (A restart keeps this object but not its drawings.)
    this.moleLeft = -1;
    this.moleTiles = [];
    this.moleMarks = null;
    // A campaign lawn past the free ones, without the full game (any way in, e.g. NEXT LAWN on
    // the results): the offer instead.
    const list = this.levelData.playlist;
    if (
      list?.kind === 'campaign' &&
      !list.back &&
      !this.levelData.daily &&
      needsFullGame(saves().save, list.index + 1)
    ) {
      this.scene.start('Unlock', {});
      return;
    }
    const { level, mowerId, daily } = this.levelData;
    const mower = daily ? MOWER_DEFS[mowerId] : effectiveMower(saves().save, mowerId);
    this.layoutCamera();
    this.scale.on(Phaser.Scale.Events.RESIZE, this.layoutCamera);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off(Phaser.Scale.Events.RESIZE, this.layoutCamera);
      this.scene.stop('Hud');
      this.scene.stop('Results');
      gameAudio.stopMower();
      gameAudio.setNight(false);
      this.ghost?.destroy();
      this.ghost = null;
    });

    // Night falls on a Lights Out lawn: no birdsong, the breeze up.
    gameAudio.setNight(!!level.sim.dark);

    const run = this.levelData.endless;
    // Daily loaners go without tools, so everyone plays the same day. A TRY IT lawn has just its
    // own item.
    const practice = this.levelData.practice?.item;
    const toolIds = practice
      ? practice.kind === 'tool'
        ? [practice.id]
        : []
      : daily
        ? []
        : loadout(saves().save, mowerId);
    this.toolCount = toolIds.length;
    // Boosts in stock come along too (not to the daily lawn, for the same reason).
    this.boostIds = practice
      ? practice.kind === 'boost'
        ? [practice.id]
        : []
      : daily
        ? []
        : BOOST_IDS.filter((id) => boostStock(saves().save, id) > 0);
    this.boxItems = !!daily && !practice && (level.sim.pickups ?? []).some((p) => isItemId(p.kind));
    this.layoutCamera();
    this.sim = new Simulation(
      level.sim,
      mower,
      run ? { hp: run.hp, fuel: run.fuel } : {},
      toolIds.map((id) =>
        simTool(upgradedTool(TOOL_BY_ID[id], toolLevel(saves().save, id)), level.art),
      ),
    );
    this.view = new LevelView(this, this.sim, level.art, {
      reducedShake: saves().save.settings.reducedShake,
      biome: this.biome(),
      paint: equippedPaint(saves().save, mowerId),
      stripes: equippedStripes(saves().save),
      master: !daily && mowerMastered(saves().save, mowerId),
      yard: this.yard,
    });
    this.view.applyEvents(this.sim.drainEvents());
    // Racing the Daily leader: their run, replayed alongside as a see-through mower.
    const race = this.levelData.ghost;
    const community = this.levelData.playlist?.community;
    const featured = this.levelData.playlist?.featured;
    this.ghost =
      race && (daily || community || featured)
        ? GhostRun.create(this, level.sim, race.run, MOWER_DEPTH - 1, level.art)
        : null;

    this.scheme = loadControlScheme(saves().save.settings.controls);
    this.touchDebug = TouchDebug.enabled()
      ? new TouchDebug(this, () => ({
          pending: this.sim.mower.pendingIntent,
          queued: this.sim.mower.queuedIntent,
        }))
      : null;
    this.controls = new InputController(
      this,
      this.scheme,
      { onActivity: this.onActivity, onIntent: this.onIntent, onTrace: this.touchDebug?.trace },
      (y) =>
        y < HUD_HEIGHT
          ? 'ui'
          : this.barSlots() > 0 && y > this.scale.gameSize.height - TOOLBAR_HEIGHT
            ? 'swipe'
            : null,
    );
    const keyboard = this.input.keyboard;
    keyboard?.on('keydown-C', this.toggleScheme);
    keyboard?.on('keydown-P', this.toggleAutopilot);
    keyboard?.on('keydown-SPACE', this.toggleThrottle);
    keyboard?.on('keydown-T', this.toggleThrottle);
    keyboard?.on('keydown-ONE', () => this.useTool(0));
    keyboard?.on('keydown-TWO', () => this.useTool(1));
    keyboard?.on('keydown-THREE', () => this.useBoost(0));
    keyboard?.on('keydown-FOUR', () => this.useBoost(1));
    keyboard?.on('keydown-FIVE', () => this.useBoost(2));
    keyboard?.on('keydown-SIX', () => this.useBoost(3));
    keyboard?.on('keydown-R', () => this.restart());
    keyboard?.on('keydown-ESC', () => this.pause());
    // A mower that never stops must not keep going while the app is in the background.
    const stopWatching = onAppHidden(() => this.pause());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, stopWatching);

    this.scene.launch('Hud', {
      // The pause button only pauses (owner, build 14: a tap there while paused or during the
      // countdown left for the menu); the pause card and the results have their own buttons.
      onMenu: () => this.pause(),
      onThrottle: this.toggleThrottle,
      onTool: (slot: number) => this.useTool(slot),
      toolLabels: this.boxItems
        ? [TOOL_BY_ID.stringTrimmer.short]
        : toolIds.map((id) => TOOL_BY_ID[id].short),
      toolIcons: this.boxItems ? ['stringTrimmer'] : toolIds,
      onBoost: (i: number) => this.useBoost(i),
      boostIcons: this.boxItems ? ['turbo'] : this.boostIds,
      // Lawn Art on a show lawn: the picture's reference card.
      ...(this.sim.level.art && !this.sim.level.art.goal
        ? { artPicture: artRows(this.sim.level.art.pattern, this.sim.grid.width) }
        : {}),
    });
    this.scene.bringToTop('Hud');
    const save = saves().save;
    // Campaign lawns list their star goals, lit where already earned.
    const campaign = this.levelData.playlist?.kind === 'campaign' && !this.levelData.daily;
    const earned = save.levels[this.levelData.level.id]?.stars ?? 0;
    const goals = campaign
      ? starGoals(this.levelData.level, this.levelData.mowerId, this.engine()).goals.map(
          ([bit, text]) => ({
            text,
            earned: (earned & bit) !== 0,
          }),
        )
      : [];
    const storm = this.levelData.level.sim.storm;
    const art = this.levelData.level.sim.art;
    const herd = this.levelData.level.sim.herd;
    // The boss's goal, in a line.
    const sim = this.levelData.level.sim;
    const goal = storm
      ? goals.length > 0
        ? 'Beat the rain to the shed.'
        : `Beat the rain to the shed. Mow ${Math.round(storm.target * 100)}% for a star.`
      : art
        ? "Mow the picture (follow the ticks), then the judges' table."
        : sim.soccer
          ? `Drive into the ball. Score ${sim.soccer.target} by halftime!`
          : herd
            ? 'Pen the sheep. Mow it all.'
            : sim.rival
              ? "Out-mow the robot. Don't bump it."
              : sim.dark
                ? 'Light lanterns. Mow it all.'
                : sim.party
                  ? 'Mow the maze before the party.'
                  : '';
    // The mower's line only where it helps: off the campaign, or the first lawn with that mower.
    const newMower = !save.mowersDriven.includes(this.levelData.mowerId);
    this.startCard = {
      title: this.levelTitle(),
      ...(goal ? { goal } : {}),
      goals,
      notes: this.levelData.practice
        ? [this.levelData.practice.hint]
        : [
            ...(this.ghost && this.levelData.ghost
              ? [
                  `Racing ${nameText(this.levelData.ghost.name)}: ${formatTime(this.levelData.ghost.ticks, TICK_RATE)}`,
                ]
              : []),
            ...(!campaign || newMower ? [`${mower.name}: ${mower.blurb}`] : []),
            ...(hasTallGrass(this.levelData.level.art)
              ? [tallGrassNote(this.sim.mowerSpec.bite)]
              : []),
          ],
      // How to steer, for the first few lawns; after that just how to start.
      hint: save.stats.lawnsMowed < 3 ? this.startHint() : 'Tap to go.',
    };
    // A retry goes straight to the countdown (once the HUD has started, so it shows the 3).
    if (this.quickStart) this.time.delayedCall(50, () => this.startCountdown());
    else this.showStartPrompt();

    exposeDebug({ scene: this, sim: this.sim });
  }

  override update(_time: number, delta: number): void {
    if (this.phase === 'playing' && this.revealHold > 0) {
      // A secret yard appearing: everything waits until it is in full view.
      this.revealHold -= delta;
    } else if (this.phase === 'playing') {
      const ticks = this.stepper.advance(delta * this.speed);
      for (let i = 0; i < ticks && this.sim.status === 'running' && this.revealHold <= 0; i++) {
        if (this.autopilot) this.steerAutopilot();
        this.handleEvents(this.sim.step());
        this.ghost?.step();
      }
      gameAudio.updateMower(this.sim);
      if (this.sim.status !== 'running') this.beginEnding();
    }
    this.view.render(this.phase === 'playing' ? this.stepper.alpha : 0, this.phase === 'playing');
    this.ghost?.render(this.phase === 'playing' ? this.stepper.alpha : 0);
    this.touchDebug?.render();
    this.updateHud();
    this.updateMoleHint(_time);
  }

  /**
   * Moles: when only molehills are left to mow (they come up on mowed lawn, so a lawn can sit at
   * 99% with one left; owner, 2026-10-29: it looked bugged), a caption says so and they blink.
   */
  private updateMoleHint(time: number): void {
    const on = this.phase === 'playing' && this.sim.status === 'running' && !!this.sim.level.moles;
    if (!on) {
      if (this.moleLeft !== -1) {
        this.moleLeft = -1;
        this.moleTiles = [];
        this.moleMarks?.clear();
        this.hud()?.showCaption(null);
      }
      return;
    }
    const grid = this.sim.grid;
    if (grid.grassRemaining !== this.moleLeft) {
      const hadHint = this.moleTiles.length > 0;
      this.moleLeft = grid.grassRemaining;
      const hills: { x: number; y: number }[] = [];
      const tiles = grid.rawTiles();
      for (let i = 0; i < tiles.length; i++) {
        if (tiles[i] === Tile.Molehill)
          hills.push({ x: i % grid.width, y: Math.floor(i / grid.width) });
      }
      this.moleTiles = hills.length > 0 && hills.length === this.moleLeft ? hills : [];
      if (this.moleTiles.length > 0) {
        this.hud()?.showCaption(
          this.moleTiles.length === 1 ? 'Flatten the molehill!' : 'Flatten the molehills!',
          true,
        );
      } else if (hadHint) this.hud()?.showCaption(null);
    }
    if (this.moleTiles.length === 0) {
      this.moleMarks?.clear();
      return;
    }
    this.moleMarks ??= this.add.graphics().setDepth(MOWER_DEPTH - 1);
    const g = this.moleMarks.clear();
    const alpha = 0.55 + 0.45 * Math.sin(time / 140);
    g.lineStyle(1, UI.accent, alpha);
    for (const { x, y } of this.moleTiles) {
      g.strokeRect(x * TILE_SIZE + 0.5, y * TILE_SIZE + 0.5, TILE_SIZE - 1, TILE_SIZE - 1);
    }
  }

  // ── Flow ──────────────────────────────────────────────────────────────────────────────────

  /**
   * The first input starts the 3-2-1. A steering input (a swipe, an arrow key) also sets the
   * direction the mower sets off in, like any other swipe during the countdown; a plain touch or
   * tap only starts it.
   */
  private readonly onActivity = (steering: boolean): boolean => {
    if (this.phase !== 'ready') return false;
    this.hud()?.hideMessage();
    this.startCountdown();
    return !steering;
  };

  private readonly onIntent = (intent: Intent): void => {
    if (this.phase === 'countdown' && (intent.type === 'face' || intent.type === 'turn')) {
      this.queuedTurn = intent;
      this.view.previewTurn(intent);
      return;
    }
    if (this.phase === 'playing' && !this.autopilot) this.sim.enqueue(intent);
  };

  private beginEnding(): void {
    this.phase = 'ending';
    const before = saves().save.coins;
    const model = this.withReveals(this.withCoinFlight(this.settle(), before));
    const won = this.sim.status === 'won';
    const show = () => {
      if (this.phase !== 'ending') return;
      this.phase = 'ended';
      this.input.off('pointerup', skip);
      this.scene.launch('Results', model);
      this.scene.bringToTop('Results');
    };
    // A tap skips the look over the lawn.
    const skip = () => {
      this.flight?.stop();
      this.flight = null;
      show();
    };
    if (!won) {
      this.time.delayedCall(END_PROMPT_DELAY_MS, show);
      return;
    }
    // A clear: the results cheer, there is time to admire the lawn, and first a look over it
    // (owner, 2026-10-05).
    model.cheer = true;
    const primary = model.buttons.findIndex((b) => b.primary);
    model.buttons.splice(primary < 0 ? model.buttons.length : primary, 0, {
      label: 'Admire',
      action: () => this.admire(),
    });
    this.time.delayedCall(WIN_PROMPT_DELAY_MS / 2, () => {
      if (this.phase !== 'ending') return;
      this.input.once('pointerup', skip);
      if (reducedMotion()) {
        this.view.shine();
        this.time.delayedCall(900, show);
      } else {
        this.flight = this.view.flyover(() => this.time.delayedCall(250, show));
      }
    });
  }

  /**
   * Milestones join the attempt's reveals (before a finished zone, which leads on), and the
   * results' buttons play the reveals before doing what they do (ADMIRE excepted).
   */
  private withReveals(model: ResultsModel): ResultsModel {
    let milestones: Reveal[] = [];
    saves().update((s) => (milestones = takeMilestones(s)));
    const zone = this.reveals.findIndex((r) => r.kind === 'zoneComplete');
    this.reveals.splice(zone < 0 ? this.reveals.length : zone, 0, ...milestones);
    if (this.tagReveal) this.reveals.unshift(this.tagReveal);
    this.tagReveal = null;
    return {
      ...model,
      buttons: model.buttons.map((b) =>
        b.label === 'Admire' ? b : { ...b, action: () => this.afterReveals(b.action, b.goes) },
      ),
    };
  }

  /**
   * Plays the waiting reveal cards over everything, then `action` (or, if a card's SEE IT IN THE
   * GARAGE was tapped, the Garage on that item, offering the lawn `action` was going to, `goes`).
   */
  private afterReveals(action: () => void, goes?: 'next' | 'retry'): void {
    const reveals = this.reveals;
    this.reveals = [];
    if (reveals.length === 0) {
      action();
      return;
    }
    this.scene.sleep('Results');
    const data: RevealData = {
      reveals,
      done: (exit) => {
        if (!exit) {
          this.scene.wake('Results');
          action();
          return;
        }
        if ('unlock' in exit) {
          wipeTo(this, 'Unlock', { backTo: 'Menu' });
          return;
        }
        this.registry.set(REG_GARAGE_OPEN, exit.garage);
        // One tap from the Garage back into the action (owner, 2026-10-10). Not mid-run in an
        // endless mode: its run goes on from its own results.
        const resume = this.levelData.endless
          ? null
          : goes === 'next'
            ? this.nextData()
            : goes === 'retry'
              ? this.levelData
              : null;
        if (resume) this.registry.set(REG_GARAGE_RESUME, resume);
        wipeTo(this, 'Garage');
      },
    };
    this.scene.launch('Reveal', data);
    this.scene.bringToTop('Reveal');
  }

  /**
   * Sends the coins this attempt earned flying from the results into the HUD's coin counter,
   * which holds its old value until they land.
   */
  private withCoinFlight(model: ResultsModel, before: number): ResultsModel {
    const hud = this.hud();
    if (saves().save.coins <= before || !hud) return model;
    this.walletShown = before;
    return {
      ...model,
      coinFlight: {
        from: before,
        target: hud.walletTarget(),
        onLand: (wallet) => {
          this.walletShown = wallet;
          if (wallet !== null) hud.popWallet();
        },
      },
    };
  }

  /**
   * Admiring the finished lawn: the results step aside, and a drag looks around the lawn; a tap
   * brings the results back.
   */
  private admire(): void {
    this.scene.sleep('Results');
    this.view.beginAdmire();
    this.hud()?.showCaption('Drag to look. Tap when done.');
    let last: { x: number; y: number } | null = null;
    let moved = 0;
    const down = (p: Phaser.Input.Pointer) => {
      last = { x: p.x, y: p.y };
      moved = 0;
    };
    const move = (p: Phaser.Input.Pointer) => {
      if (!last || !p.isDown) return;
      const c = this.view.admireCenter();
      // Drag the lawn: it follows the finger.
      this.view.admireTo(c.x - (p.x - last.x), c.y - (p.y - last.y));
      moved += Math.abs(p.x - last.x) + Math.abs(p.y - last.y);
      last = { x: p.x, y: p.y };
    };
    const up = () => {
      last = null;
      if (moved > 6) return;
      this.input.off('pointerdown', down);
      this.input.off('pointermove', move);
      this.input.off('pointerup', up);
      this.hud()?.showCaption(null);
      this.scene.wake('Results');
    };
    // Next frame, so the tap that pressed ADMIRE does not end it at once.
    this.time.delayedCall(0, () => {
      this.input.on('pointerdown', down);
      this.input.on('pointermove', move);
      this.input.on('pointerup', up);
    });
  }

  /** Pays out and records the attempt, and builds the results screen. */
  private settle(): ResultsModel {
    const sim = this.sim;
    const d = this.levelData;
    const won = sim.status === 'won';
    if (won) this.postLawn();
    const mowed = sim.grid.totalGrass - sim.grid.grassRemaining;
    const time = formatTime(sim.tick, TICK_RATE);
    const failTitle =
      sim.status === 'outOfFuel'
        ? 'Out of gas!'
        : sim.status === 'caught'
          ? 'Rained out!'
          : sim.status === 'outmowed'
            ? 'The robot out-mowed you!'
            : sim.status === 'late'
              ? sim.level.soccer
                ? 'The whistle went!'
                : 'The party has started!'
              : 'Your mower broke!';
    const menu = { label: d.playlist?.back?.label ?? 'Menu', action: () => this.toMenu() };
    const retry = {
      label: 'Retry',
      action: () => this.restart(),
      primary: !won,
      goes: 'retry' as const,
    };
    const next = this.nextData();
    // (Jumping ahead after a replay: the button says where to.)
    const jumps = next?.playlist && d.playlist && next.playlist.index !== d.playlist.index + 1;
    const nextButton = next
      ? [
          {
            label: jumps ? `Lawn ${(next.playlist?.index ?? 0) + 1}` : 'Next',
            action: () => this.nextLevel(),
            primary: true,
            goes: 'next' as const,
          },
        ]
      : [];

    if (d.endless) {
      const run = d.endless;
      const mode = run.mode ?? 'lawns';
      // Lawn Art runs also need the picture to pass the stage's mark; Rain Chase is one lawn.
      const passed = endlessStagePassed(mode, run.stage, sim);
      // This lawn's coins and bills go in the run's bank, paid out when the run ends. A lawn that
      // ends the run unfinished keeps nothing (Rain Chase always ends with the rain).
      const lawnLines = endlessLawnLines(this.attemptResult(mowed), won || mode === 'rain');
      this.runBank = bankLines(run.bank ?? [], lawnLines);
      saves().update((s) => {
        s.stats.tilesMowed += mowed;
        if (won) s.stats.lawnsMowed += 1;
      });
      if (passed) {
        // (The app may close on the results: this lawn counts.)
        this.keepRunOpen(run.stage, run.score + this.stageScore(true));
        return {
          title: `${mode === 'lawns' ? 'Lawn' : 'Stage'} ${run.stage} cleared!`,
          ...(sim.level.art ? { judges: judgeScores(sim.artMatch() ?? 0) } : {}),
          info: [
            `Score ${run.score + this.stageScore(true)}`,
            sim.level.soccer ? `Goals ${sim.goals}` : `Time ${time}`,
          ],
          coinLines: lawnLines,
          note: `${bankNote(this.runBank)}, paid when the run ends.`,
          buttons: [{ label: 'End run', action: () => this.endRun(true) }, ...nextButton],
        };
      }
      // Rain Chase always ends the run: at the shed it is a win, not a failure.
      const title =
        mode === 'rain' && won
          ? 'You beat the rain!'
          : mode === 'art' && won
            ? 'Not quite the picture. Run over.'
            : `${failTitle} Run over.`;
      return this.runSummary(
        title,
        run.score + this.stageScore(won),
        run.stage - 1,
        !(mode === 'rain' && won),
      );
    }

    if (d.daily) {
      if (!won) {
        return {
          title: failTitle,
          titleColor: UI.danger,
          info: [`${sim.grid.percentMowed}% mowed`, ...this.ghostLine()],
          coinLines: [],
          buttons: [menu, retry],
        };
      }
      // A challenge on an earlier day's lawn: only the race counts (no Daily reward, best or
      // streak for a day that is over).
      if (d.challenge && d.daily !== todayKey()) {
        return {
          title: 'Challenge done!',
          info: [`Time ${time}`, ...this.ghostLine()],
          coinLines: [],
          buttons: [menu, { ...retry, primary: false }],
        };
      }
      const firstToday = dailyBestTicks(d.daily) === null;
      const isBest = submitDailyTicks(d.daily, sim.tick);
      syncDailyReminders(); // today's reminder is no longer needed
      const streak = saves().save.daily.streak.count;
      const reward = dailyReward(firstToday, streak);
      const save = saves().update((s) => {
        s.coins += reward;
        s.stats.lawnsMowed += 1;
        s.stats.tilesMowed += mowed;
      });
      const best = dailyBestTicks(d.daily) ?? sim.tick;
      return {
        title: 'Daily complete!',
        ...(isBest && !firstToday ? { banner: 'NEW BEST TIME!' } : {}),
        info: [
          `Time ${time}${isBest ? ' (best!)' : ''}`,
          `Best ${formatTime(best, TICK_RATE)}`,
          `Streak ${streak}`,
          ...this.ghostLine(),
        ],
        // Online: today's place on the board, which opens it (CLUB > BOARDS).
        ...(onlineOn() && this.lastRun
          ? {
              rank: {
                load: boardResultFor(this.lastRun, 'daily').then((r) =>
                  r ? rankText(r, 'today') : null,
                ),
                open: () => this.toBoard({ open: 'daily' }),
              },
            }
          : {}),
        coinLines: reward > 0 ? [{ label: 'Daily reward', coins: reward }] : [],
        wallet: save.coins,
        buttons: [
          menu,
          { ...retry, primary: false },
          // Online: send this time to a friend to beat.
          ...(onlineOn() && this.lastRun
            ? [{ label: 'Challenge', action: () => void this.challengeFriend(), primary: true }]
            : []),
        ],
      };
    }

    const coinsInfo = sim.totalCoins > 0 ? [`Coins ${sim.coins}/${sim.totalCoins}`] : [];
    const soccerInfo = sim.level.soccer
      ? [
          `Goals: ${sim.goals} (${sim.level.soccer.target} to win)`,
          `${sim.grid.percentMowed}% of the pitch mowed`,
        ]
      : null;
    const score = sim.rivalScore();
    const rivalInfo = score
      ? [
          `You ${score.you} tiles, robot ${score.robot}`,
          `${Math.round((score.you * 100) / Math.max(1, sim.grid.totalGrass))}% of the lawn`,
        ]
      : [];
    if (d.playlist?.kind !== 'campaign') {
      // Test lawns: no rewards, just the result (a community lawn's or the featured lawn's place
      // on its board, online).
      const community = d.playlist?.community;
      const featured = d.playlist?.featured;
      return {
        title: won ? (soccerInfo ? 'What a match!' : 'Lawn complete!') : failTitle,
        ...(won ? {} : { titleColor: UI.danger }),
        info: soccerInfo ?? [
          ...(won ? [`Time ${time}`, ...coinsInfo] : [`${sim.grid.percentMowed}% mowed`]),
          ...rivalInfo,
          ...this.ghostLine(),
        ],
        ...(community && onlineOn() && this.lastRun
          ? {
              rank: {
                load: boardResultFor(this.lastRun, communityBoard(community.id)).then((r) =>
                  r ? rankText(r, 'on this lawn') : null,
                ),
              },
            }
          : {}),
        ...(featured && onlineOn() && this.lastRun
          ? {
              rank: {
                load: boardResultFor(this.lastRun, FEATURED).then((r) =>
                  r ? rankText(r, 'this week') : null,
                ),
                open: () => this.toBoard({ open: 'featured' }),
              },
            }
          : {}),
        coinLines: [],
        buttons: won ? [menu, { ...retry, primary: false }, ...nextButton] : [menu, retry],
      };
    }

    // Anything but a clean clear lets the rare find get away (it wanders to another lawn).
    const escaped = !won && !!this.rareFind && !this.rareGone;
    if (escaped) this.rareFindEscapes();
    const attempt: AttemptResult = {
      levelId: d.level.id,
      ...(d.level.level !== undefined ? { level: d.level.level } : {}),
      won,
      ticks: sim.tick,
      ...(d.level.par[d.mowerId] !== undefined ? { parTicks: d.level.par[d.mowerId] } : {}),
      coinsPicked: sim.coins,
      totalCoins: sim.totalCoins,
      spareCoins: sim.spareCoins,
      trampled: sim.trampled,
      smashed: sim.smashed,
      smashedTiers: [...sim.smashedTiers],
      hives: sim.hives,
      chewed: sim.chewed,
      dogBumps: sim.dogBumps,
      sprinklersBroken: sim.sprinklersBroken,
      // A continued attempt already counted its earlier tiles when it failed.
      mowedTiles: mowed - this.mowedRecorded,
      treasure: sim.treasure,
      yardFound: this.yardOpened,
      yardTotal: secretYards().size,
      rarePicked: sim.rareCollected && !this.rareGone,
      ...(this.rareFind?.wandering ? { rareItem: this.rareFind.item } : {}),
      challenge: challengeFor(d.level),
      heartsLost: this.heartsLost,
      critterBumps: this.critterBumps,
      toolsUsed: this.toolsUsed,
      boostsUsed: this.boostsUsed,
      engine: this.engine(),
      ...(d.level.goldTime?.[d.mowerId] !== undefined
        ? { goldTicks: d.level.goldTime[d.mowerId] }
        : {}),
      ...(this.continuesUsed > 0 ? { continued: true } : {}),
      ...(sim.level.storm
        ? {
            bossTarget: sim.level.storm.target,
            mowedShare: mowed / Math.max(1, sim.grid.totalGrass),
          }
        : {}),
      ...(sim.level.rival
        ? {
            bossTarget: sim.level.rival.target,
            mowedShare: (sim.rivalScore()?.you ?? 0) / Math.max(1, sim.grid.totalGrass),
          }
        : {}),
      ...(sim.level.art ? { artMatch: sim.artMatch() ?? 0, artStars: sim.level.art.stars } : {}),
      ...(sim.level.soccer
        ? {
            goals: sim.goals,
            soccerStars: sim.level.soccer.stars,
            mowedShare: mowed / Math.max(1, sim.grid.totalGrass),
          }
        : {}),
    };
    this.mowedRecorded = mowed;
    const before = saves().save;
    const progressBefore = campaignProgress(before);
    const previousStars = before.levels[d.level.id]?.stars ?? 0;
    const previousBest = before.levels[d.level.id]?.bestTicks;
    const newBest = won && previousBest !== undefined && sim.tick < previousBest;
    const settlement = settleAttempt(before, attempt);
    const save = saves().update((s) => applySettlement(s, attempt, settlement));
    // The moments worth a screen of their own, played when leaving the results.
    this.reveals = campaignReveals(before, save, {
      level: d.level.level ?? 0,
      firstClear: won && settlement.firstClear,
      zoneReward: settlement.zoneReward,
      grandReward: settlement.grandReward,
      yardsReward: settlement.yardsReward,
      clearedReward: settlement.clearedReward,
      rareItem: settlement.rareItem,
    });
    const unlocked = MOWER_IDS_BY_UNLOCK.find(
      (id) => MOWER_DEFS[id].unlockLevel > progressBefore && mowerOpen(save, id),
    );
    const rareName = settlement.rareItem
      ? COSMETICS.find((c) => c.id === settlement.rareItem)?.name
      : undefined;
    // Continue (campaign lawns): pay to carry on from here, the price doubling each time.
    // (Not after the storm: the rain doesn't wait.)
    const cost =
      !won &&
      sim.status !== 'caught' &&
      sim.status !== 'outmowed' &&
      sim.status !== 'late' &&
      d.level.level !== undefined
        ? continueCost(d.level.level, this.continuesUsed)
        : null;
    const canContinue = cost !== null && save.coins >= cost;
    const found = sim.coins + sim.spareCoins;
    const zone = settlement.zoneReward;
    const note = settlement.grandReward
      ? 'Every star in the game! Stellar Tie-Dye unlocked!'
      : settlement.yardsReward
        ? 'Every secret yard! Secret Garden unlocked!'
        : settlement.clearedReward
          ? 'Every lawn mowed! Finish Line Paint unlocked!'
          : zone
            ? `All stars! ${COSMETICS.find((c) => c.id === zone.cosmetic)?.name ?? ''} unlocked!`
            : rareName
              ? `Rare find kept: ${rareName}!`
              : escaped
                ? 'The rare find got away!'
                : unlocked
                  ? `${MOWER_DEFS[unlocked].name} now in the Garage!`
                  : undefined;
    return {
      lawn: `Lawn ${this.lawnNumber()}: ${d.level.name}`,
      title: won
        ? sim.level.storm
          ? 'You beat the storm!'
          : sim.level.art
            ? 'The judges are in!'
            : sim.level.soccer
              ? 'What a match!'
              : sim.level.herd
                ? 'All safely penned!'
                : sim.level.rival
                  ? 'You beat the robot!'
                  : sim.level.dark
                    ? 'Lights on, lawn done!'
                    : sim.level.party
                      ? 'Ready for the party!'
                      : 'Lawn complete!'
        : failTitle,
      ...(won ? {} : { titleColor: UI.danger }),
      ...(zone ? { banner: 'ALL STARS!' } : newBest ? { banner: 'NEW BEST TIME!' } : {}),
      ...(won && sim.level.art ? { judges: judgeScores(sim.artMatch() ?? 0) } : {}),
      ...(won
        ? {
            stars: {
              bits: settlement.stars | previousStars,
              fresh: settlement.stars & ~previousStars,
            },
            goals: starGoals(d.level, d.mowerId, this.engine()).goals,
          }
        : {}),
      info: sim.level.art
        ? [`Picture: ${Math.round((sim.artMatch() ?? 0) * 100)}% match`]
        : soccerInfo
          ? soccerInfo
          : sim.level.storm
            ? [
                `${sim.grid.percentMowed}% mowed (goal ${Math.round(sim.level.storm.target * 100)}%)`,
              ]
            : sim.level.herd && !won
              ? [`${sim.grid.percentMowed}% mowed`, `${sim.sheepCount()?.penned ?? 0} sheep penned`]
              : sim.level.rival
                ? rivalInfo
                : won
                  ? [`Time ${time}`, ...coinsInfo]
                  : [`${sim.grid.percentMowed}% mowed`],
      // Online: this lawn's place with this mower, which opens its board.
      ...(won && onlineOn() && this.lastRun && d.level.level !== undefined
        ? {
            rank: {
              load: boardResultFor(
                this.lastRun,
                // (Soccer plays to the whistle: its board ranks goals, then grass mowed.)
                isSoccerLawn(d.level.level)
                  ? goalsBoard(d.level.level, d.mowerId)
                  : lawnBoard(d.level.level, d.mowerId),
              ).then((r) => (r ? rankText(r, 'with this mower') : null)),
              open: () => this.toBoard({ open: { lawn: d.level.level!, mower: d.mowerId } }),
            },
          }
        : {}),
      // While a continue is on offer the picked-up coins are not lost yet.
      coinLines: canContinue ? settlement.lines.filter((l) => l.coins >= 0) : settlement.lines,
      wallet: save.coins,
      ...(note ? { note } : {}),
      buttons: won ? [menu, { ...retry, primary: false }, ...nextButton] : [menu, retry],
      // Continuing is a paid option, boxed apart; Retry (free) stays the default.
      ...(cost !== null
        ? {
            offer: {
              title: canContinue ? 'Continue from here?' : 'Not enough to continue',
              detail:
                found > 0
                  ? `Keeps your ${found} coin${found === 1 ? '' : 's'}`
                  : 'Pick up where you stopped',
              cost,
              action: canContinue ? () => this.continueRun(cost) : null,
            },
          }
        : {}),
    };
  }

  /**
   * Pays for a continue and carries on from where the mower stopped: full health and tank, then
   * the 3-2-1 with a free choice of direction (it is usually nose-first against something).
   */
  private continueRun(cost: number): void {
    let paid = false;
    saves().update((s) => (paid = payForContinue(s, cost)));
    if (!paid) return;
    this.continuesUsed++;
    this.scene.stop('Results');
    gameAudio.play('coin', 1.2);
    this.sim.enqueue({ type: 'revive' });
    this.handleEvents(this.sim.step());
    this.startCountdown();
  }

  private showStartPrompt(): void {
    this.hud()?.showStartCard(this.startCard);
  }

  /** Android back button: pause a running lawn; from the pause or results screen, go to the menu. */
  onBack(): void {
    if (this.phase === 'playing' || this.phase === 'ready') this.pause();
    else this.toMenu();
  }

  /** Freezes the attempt and shows Resume / Retry / Menu. */
  private pause(): void {
    if (this.phase !== 'playing' && this.phase !== 'ready' && this.phase !== 'countdown') return;
    const wasReady = this.phase === 'ready';
    if (this.phase === 'countdown') this.cancelCountdown();
    this.phase = 'paused';
    gameAudio.stopMower();
    this.hud()?.hideMessage();
    // Endless: no retrying a lawn mid-run; ending the run pays out what it has banked.
    const run = this.levelData.endless;
    const model: ResultsModel = {
      title: 'Paused',
      // Which lawn this is (owner, build 14), then how far along.
      info: [this.pauseLawnLine(), `${this.sim.grid.percentMowed}% mowed`],
      coinLines: [],
      ...(run ? { note: this.pauseBankNote() } : {}),
      buttons: run
        ? [
            { label: 'End run', action: () => this.endRun(false) },
            { label: 'Resume', action: () => this.resume(wasReady), primary: true },
          ]
        : [
            { label: 'Menu', action: () => this.toMenu() },
            { label: 'Retry', action: () => this.restart() },
            { label: 'Resume', action: () => this.resume(wasReady), primary: true },
          ],
    };
    this.scene.launch('Results', model);
    this.scene.bringToTop('Results');
  }

  private resume(wasReady: boolean): void {
    this.scene.stop('Results');
    this.stepper.reset();
    if (wasReady) {
      this.phase = 'ready';
      this.showStartPrompt();
    } else {
      this.startCountdown();
    }
  }

  /**
   * 3, 2, 1, GO over the frozen lawn so the player can see everything before play starts or
   * resumes. A swipe during it is remembered (and shown with the turn hint) and sent to the sim at
   * GO. On the start tile any open direction is allowed, even straight back (the sim's free
   * placement rule).
   */
  private startCountdown(): void {
    this.phase = 'countdown';
    this.queuedTurn = null;
    this.view.previewTurn(null);
    ['3', '2', '1'].forEach((text, i) =>
      this.countdownTimers.push(
        this.time.delayedCall(i * COUNTDOWN_STEP_MS, () => {
          this.hud()?.showCountdown(text);
          gameAudio.play('click', 1 + i * 0.12);
        }),
      ),
    );
    this.countdownTimers.push(
      this.time.delayedCall(3 * COUNTDOWN_STEP_MS, () => this.finishCountdown()),
    );
  }

  private finishCountdown(): void {
    this.countdownTimers = [];
    this.hud()?.showCountdown('GO!');
    gameAudio.play('click', 1.5);
    // Mower soccer: the referee blows for the kick-off (not on resuming from pause).
    if (this.sim.level.soccer && this.sim.tick === 0) gameAudio.kickOff();
    // This mower has now been on a lawn: the start card stops introducing it.
    const mowerId = this.levelData.mowerId;
    if (!this.levelData.practice && !saves().save.mowersDriven.includes(mowerId)) {
      saves().update((s) => {
        s.mowersDriven.push(mowerId);
      });
    }
    this.time.delayedCall(350, () => {
      if (this.phase === 'playing') this.hud()?.hideCountdown();
    });
    this.phase = 'playing';
    this.stepper.reset();
    gameAudio.startMower(this.sim, this.levelData.mowerId);
    if (this.queuedTurn && !this.autopilot) this.sim.enqueue(this.queuedTurn);
    this.queuedTurn = null;
    this.view.previewTurn(null);
  }

  private cancelCountdown(): void {
    for (const t of this.countdownTimers) t.remove();
    this.countdownTimers = [];
    this.queuedTurn = null;
    this.view.previewTurn(null);
    this.hud()?.hideCountdown();
  }

  private restart(): void {
    this.abandonAttempt();
    this.scene.restart({ ...this.levelData, quickStart: true });
  }

  /** Leaving a lawn mid-attempt is not a clean clear: its rare find gets away. */
  private abandonAttempt(): void {
    const playing = this.phase === 'playing' || this.phase === 'paused';
    if (playing && this.sim.status === 'running' && this.sim.tick > 0) this.rareFindEscapes();
  }

  /** The lawn's rare find gets away (an attempt without a clean clear): off to another lawn. */
  private rareFindEscapes(): void {
    const find = this.rareFind;
    const list = this.levelData.playlist;
    if (!find || this.rareGone || list?.kind !== 'campaign') return;
    this.rareGone = true;
    saves().update((s) =>
      rareFindEscapes(s, {
        levelId: this.levelData.level.id,
        lawn: list.index + 1,
        item: find.item,
        wandering: find.wandering,
      }),
    );
    this.view.rareGotAway();
  }

  /**
   * Score for this endless lawn: every blade mowed (yours, against the robot) and a bonus for
   * clearing it; Lawn Art scores the picture; Rain Chase, the rows climbed.
   */
  private stageScore(cleared: boolean): number {
    return endlessStageScore(this.levelData.endless?.mode ?? 'lawns', this.sim, cleared);
  }

  /** Rain Chase: this lawn's coins and bills, which the run keeps however it ends. */
  private rainLawnLines(): CoinLine[] {
    const mowed = this.sim.grid.totalGrass - this.sim.grid.grassRemaining;
    return endlessLawnLines(this.attemptResult(mowed), true);
  }

  /**
   * The pause card's note on an endless run: the bank, and this lawn's coins (the HUD's green
   * count). Rain Chase keeps them however the run ends; other modes once the lawn is cleared.
   */
  private pauseBankNote(): string {
    if (this.levelData.endless?.mode === 'rain') {
      return `${bankNote(bankLines(this.runBank, this.rainLawnLines()))} so far`;
    }
    const lawn = this.sim.coins + this.sim.spareCoins;
    return lawn > 0
      ? `${bankNote(this.runBank)}, +${lawn} once this lawn is cleared`
      : `${bankNote(this.runBank)} so far`;
  }

  /** Rain Chase: rows climbed from the start. */
  private rowsClimbed(): number {
    return endlessRowsClimbed(this.sim);
  }

  private nextData(): LevelSceneData | null {
    const run = this.levelData.endless;
    if (run) {
      return {
        level: endlessStage(
          run.mode ?? 'lawns',
          run.runSeed,
          run.stage + 1,
          this.levelData.mowerId,
        ),
        mowerId: this.levelData.mowerId,
        endless: {
          ...(run.mode ? { mode: run.mode } : {}),
          runSeed: run.runSeed,
          ...(run.firstLawnFor ? { firstLawnFor: run.firstLawnFor } : {}),
          stage: run.stage + 1,
          score: run.score + this.stageScore(true),
          bank: this.runBank,
          lawns: [...(run.lawns ?? []), postedLawn(this.sim)],
          ...(run.assisted || this.autopilotUsed ? { assisted: true } : {}),
          // Its hearts, and a quick top-up between lawns: half a tank on top of what's left.
          ...endlessCarry(this.sim),
        },
      };
    }
    if (this.levelData.daily) return null;
    const list = this.levelData.playlist;
    if (!list) return null;
    const index = this.nextIndex(list);
    const level = list.levels[index];
    if (!level) return null;
    const allowed = allowedMowers(level.mowerRule);
    const mowerId = allowed.includes(this.levelData.mowerId) ? this.levelData.mowerId : allowed[0];
    return mowerId ? { level, mowerId, playlist: { ...list, index } } : null;
  }

  /**
   * The lawn NEXT goes to: in the campaign, the first lawn not yet cleared (owner, 2026-10-07: a
   * replay for stars leads back to where the player is, not to the lawn after the replayed one),
   * or, with every lawn cleared, simply the next one. Other playlists go in order.
   */
  private nextIndex(list: Playlist): number {
    if (list.kind !== 'campaign' || list.back) return list.index + 1;
    // (Asked before and after this clear is saved: the lawn just played never counts as ahead.)
    const frontier = campaignProgress(saves().save);
    return frontier > list.index && frontier < list.levels.length ? frontier : list.index + 1;
  }

  private nextLevel(): void {
    const next = this.nextData();
    if (next) wipeTo(this, 'Level', next, () => this.scene.restart(next));
    else this.toMenu();
  }

  /** From the Daily's results to its board (CLUB > BOARDS). */
  private toBoard(data: BoardsData): void {
    this.abandonAttempt();
    wipeTo(this, 'Boards', data);
  }

  private toMenu(): void {
    this.abandonAttempt();
    // Leaving an endless run any other way (the back button) still pays what it banked.
    if (this.levelData.endless && !this.runPaid) this.payRun(this.runScore());
    const back = this.levelData.playlist?.back;
    wipeTo(this, back?.scene ?? 'Menu', back?.data);
  }

  /**
   * Endless, for the HUD: the run's bank plus the coins picked up on this lawn while it is in
   * play (once it is over its coins are in the bank); nothing once the run has paid out.
   */
  private runPending(): number | null {
    if (this.runPaid) return null;
    const inPlay = this.phase !== 'ending' && this.phase !== 'ended';
    return linesTotal(this.runBank) + (inPlay ? this.sim.coins + this.sim.spareCoins : 0);
  }

  /** Saves where the endless run stands (see the save's `endless.openRun`). */
  private keepRunOpen(stage: number, score: number): void {
    const mode = this.levelData.endless?.mode ?? 'lawns';
    saves().update((s) => (s.endless.openRun = { mode, stage, score, bank: this.runBank }));
  }

  /** Endless: the run's score so far (with this lawn's, once it is cleared). */
  private runScore(): number {
    const run = this.levelData.endless!;
    return run.score + (this.sim.status === 'won' ? this.stageScore(true) : 0);
  }

  /**
   * Ends an endless run by choice: after a cleared lawn (`cleared`, which counts), or from the
   * pause screen (the lawn in progress does not). Shows the run's summary.
   */
  private endRun(cleared: boolean): void {
    const run = this.levelData.endless;
    if (!run || this.runPaid) return;
    // Rain Chase ended from the pause card keeps the coins picked up on the way, as the rain would.
    if (!cleared && run.mode === 'rain' && this.sim.status === 'running') {
      this.runBank = bankLines(this.runBank, this.rainLawnLines());
    }
    this.abandonAttempt();
    this.phase = 'ended';
    const model = this.withReveals(
      this.runSummary(
        'Run complete!',
        cleared ? this.runScore() : run.score,
        cleared ? run.stage : run.stage - 1,
        false,
      ),
    );
    this.scene.get('Results').scene.restart(model);
  }

  /** Racing a ghost: how this attempt went against their time. */
  private ghostLine(): string[] {
    const race = this.levelData.ghost;
    if (!race || !this.ghost) return [];
    // A challenge or a community lawn's best: raced by name; the Daily's leader, as the leader.
    const challenge = race.kind === 'challenge' || race.kind === 'best';
    const who = challenge ? whoText(race.player, race.name) : 'the leader';
    const theirs = formatTime(race.ticks, TICK_RATE);
    if (this.sim.status !== 'won') {
      return [challenge ? `${who}: ${theirs}` : `Leader ${theirs}`];
    }
    const diff = this.sim.tick - race.ticks;
    return diff < 0
      ? [`You beat ${who}!`]
      : diff === 0
        ? [challenge ? `Tied with ${who}!` : "Tied with the leader's time!"]
        : [`${formatTime(diff, TICK_RATE)} behind ${who}`];
  }

  /** CHALLENGE A FRIEND: this Daily sent as a challenge, the link shared. */
  private async challengeFriend(): Promise<void> {
    const run = this.lastRun;
    if (!run) return;
    const url = await sendChallenge(run);
    if (!url) {
      this.hud()?.showMessage("Couldn't reach the server. Try again later.");
      return;
    }
    const time = formatTime(run.claimed.ticks, TICK_RATE);
    await shareLink('lawnmower.', `Can you beat my ${time} on today's lawnmower. Daily?`, url);
  }

  /**
   * Whether this attempt may be posted online: played by a person (never the autopilot, which any
   * build can switch on), not a TRY IT lawn, not the store screenshots. `?postAssisted` lets a dev
   * build post autopilot runs, for the online e2e.
   */
  private mayPost(assisted: boolean): boolean {
    if (this.levelData.practice || storePreview()) return false;
    if (!assisted && !this.autopilotUsed) return true;
    return import.meta.env.DEV && new URLSearchParams(window.location.search).has('postAssisted');
  }

  /** A cleared Daily, or a campaign lawn cleared without a paid Continue, goes to the boards. */
  private postLawn(): void {
    const d = this.levelData;
    if (!this.mayPost(false)) return;
    const end = { score: 0, status: this.sim.status };
    // The paint worn, so the run's ghost wears it too (owner, 2026-11-04).
    const paint = equippedPaint(saves().save, d.mowerId);
    if (d.daily) {
      const run = postedRun(
        { kind: 'daily', date: d.daily },
        d.mowerId,
        [postedLawn(this.sim)],
        end,
        undefined,
        paint,
      );
      this.lastRun = run;
      queueRun(run);
      if (d.challenge && onlineOn()) void answerChallenge(d.challenge, run);
      return;
    }
    const list = d.playlist;
    // The featured lawn: a campaign run of it (the server puts it on the featured board too).
    if (list?.featured) {
      const run = postedRun(
        { kind: 'campaign', lawn: list.featured.lawn },
        d.mowerId,
        [postedLawn(this.sim)],
        end,
        loadoutFor(saves().save, d.mowerId),
        paint,
      );
      this.lastRun = run;
      queueRun(run);
      return;
    }
    // A community lawn: its board (a clear without a paid Continue).
    if (list?.community && this.continuesUsed === 0) {
      const run = postedRun(
        { kind: 'community', id: list.community.id, code: list.community.code },
        d.mowerId,
        [postedLawn(this.sim)],
        end,
        loadoutFor(saves().save, d.mowerId),
        paint,
      );
      this.lastRun = run;
      queueRun(run);
      return;
    }
    if (list?.kind !== 'campaign' || this.continuesUsed > 0) return;
    const lawn = list.index + 1;
    if (d.level.id !== `campaign-${String(lawn).padStart(3, '0')}`) return;
    const run = postedRun(
      { kind: 'campaign', lawn },
      d.mowerId,
      [postedLawn(this.sim)],
      end,
      loadoutFor(saves().save, d.mowerId),
      paint,
    );
    this.lastRun = run;
    queueRun(run);
  }

  /** An Endless run that scored goes to the boards when it ends: every lawn it played. */
  private postRun(score: number): void {
    const run = this.levelData.endless;
    if (!run || score <= 0 || !this.mayPost(run.assisted ?? false)) return;
    const mowerId = this.levelData.mowerId;
    queueRun(
      postedRun(
        {
          kind: 'endless',
          mode: run.mode ?? 'lawns',
          seed: run.runSeed,
          ...(run.firstLawnFor ? { firstLawnFor: run.firstLawnFor } : {}),
        },
        mowerId,
        [...(run.lawns ?? []), postedLawn(this.sim)],
        { score, status: this.sim.status },
        loadoutFor(saves().save, mowerId),
        equippedPaint(saves().save, mowerId),
      ),
    );
  }

  /** Pays an endless run out: its bank plus the score and new-best bonuses. */
  private payRun(score: number): { lines: CoinLine[]; best: boolean } {
    this.runPaid = true;
    this.postRun(score);
    const best = submitEndlessScore(score, this.levelData.endless?.mode ?? 'lawns');
    const pay = endlessPayout(this.runBank, score, best);
    saves().update((s) => {
      s.coins += pay.coins;
      delete s.endless.openRun;
    });
    return { lines: pay.lines, best };
  }

  /**
   * The end of an endless run: lawns cleared, score and best, everything the run banked plus its
   * bonuses, and the new wallet. Then the menu, or straight into a new run with the same mower.
   */
  private runSummary(title: string, score: number, cleared: number, failed = true): ResultsModel {
    const mode = this.levelData.endless?.mode ?? 'lawns';
    const { lines, best } = this.payRun(score);
    return {
      title,
      ...(failed ? { titleColor: UI.danger } : {}),
      ...(best ? { banner: 'NEW BEST!' } : {}),
      info: [
        mode === 'rain'
          ? `Rows climbed: ${this.rowsClimbed()}`
          : `${mode === 'lawns' ? 'Lawns' : 'Stages'} cleared: ${cleared}`,
        `Score ${score}`,
        `Best ${endlessBest(mode)}`,
      ],
      coinLines: lines,
      wallet: saves().save.coins,
      buttons: [
        { label: 'Menu', action: () => this.toMenu() },
        { label: 'New run', action: () => this.newRun(), primary: true },
      ],
    };
  }

  /** Straight into a fresh run of the same mode, same mower. */
  private newRun(): void {
    const mode = this.levelData.endless?.mode ?? 'lawns';
    const runSeed = newRunSeed();
    const mowerId = this.levelData.mowerId;
    const next: LevelSceneData = {
      level: endlessStage(mode, runSeed, 1, mowerId),
      mowerId,
      endless: {
        ...(mode !== 'lawns' ? { mode } : {}),
        runSeed,
        firstLawnFor: mowerId,
        stage: 1,
        score: 0,
      },
    };
    wipeTo(this, 'Level', next, () => this.scene.restart(next));
  }

  /** What this attempt did, for the economy (coins and bills). */
  private attemptResult(mowed: number): AttemptResult {
    const sim = this.sim;
    const d = this.levelData;
    return {
      levelId: d.level.id,
      won: sim.status === 'won',
      ticks: sim.tick,
      coinsPicked: sim.coins,
      totalCoins: sim.totalCoins,
      spareCoins: sim.spareCoins,
      trampled: sim.trampled,
      smashed: sim.smashed,
      smashedTiers: [...sim.smashedTiers],
      hives: sim.hives,
      chewed: sim.chewed,
      dogBumps: sim.dogBumps,
      sprinklersBroken: sim.sprinklersBroken,
      mowedTiles: mowed,
      rarePicked: false,
    };
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────────────────

  private handleEvents(all: readonly SimEvent[]): void {
    // A rare find that got away isn't there to pick up any more.
    const events = this.rareGone
      ? all.filter((e) => !(e.type === 'pickup' && e.kind === 'rare'))
      : all;
    this.view.applyEvents(events);
    gameAudio.onEvents(events, this.sim);
    for (const e of events) {
      if (e.type === 'gateOpened') {
        // A secret yard found: its escaped find is at stake from now on (it may get away again).
        this.yardOpened = true;
        this.revealHold = YARD_REVEAL_HOLD_MS;
        if (this.yardRare && !this.rareFind)
          this.rareFind = { item: this.yardRare, wandering: true };
      }
      if (e.type === 'crashed') {
        this.heartsLost++;
        if (e.critter) this.bumpedCritter(e.critter);
      }
      // A squirrel or rabbit bumped poofs away (rules 6): no heart, but a bump all the same.
      if (e.type === 'critterPoofed') {
        haptics.tap();
        this.bumpedCritter(e.kind);
      }
      if (e.type === 'toolUsed') this.toolsUsed++;
      // An item box: the first one ever says how to use it.
      if (e.type === 'pickup' && isItemId(e.kind)) {
        haptics.tap();
        // (Saved only the first time: a save mid-lawn is a stall, right when a tap or turn comes.)
        const first = !(saves().save.tipsSeen ?? []).includes('itemBox');
        if (first) {
          saves().update((s) => {
            if (!(s.tipsSeen ?? []).includes('itemBox'))
              s.tipsSeen = [...(s.tipsSeen ?? []), 'itemBox'];
          });
          this.hud()?.showNotice(`Tap ${e.kind === 'turbo' ? 'TRBO' : 'TRIM'} to use it!`);
        }
      }
      // A fired boost leaves the stock for good, whatever happens next.
      // (Not on a TRY IT lawn: that one is on the house; nor a Turbo from an item box.)
      if (e.type === 'boostUsed' && !e.fromBox && !this.levelData.practice) {
        saves().update((save) => spendBoost(save, e.boost));
      }
      if (e.type === 'boostUsed' && !e.fromBox) this.boostsUsed++;
      if (e.type === 'turned' || e.type === 'trampled') haptics.tap();
      else if (e.type === 'smashed' || e.type === 'chewed' || e.type === 'sprinklerBroken')
        haptics.impact();
      else if (e.type === 'crashed') haptics.impact();
      else if (e.type === 'mowerBroken') haptics.heavy();
      else if (e.type === 'kicked') haptics.tap();
      else if (e.type === 'goal') {
        // GOAL! big under the HUD for a moment.
        haptics.heavy();
        this.hud()?.showCountdown('GOAL!');
        this.time.delayedCall(1100, () => {
          if (this.phase !== 'countdown') this.hud()?.hideCountdown();
        });
      }
    }
  }

  /** Dev/demo autopilot: lets the greedy solver drive (press P, or ?autopilot in the URL). */
  private steerAutopilot(): void {
    if (this.sim.mower.progress !== 0) return;
    const art = this.sim.level.art;
    if (art) {
      // Art lawns: paint the picture, then drive onto the judges' table.
      this.artPilot ??= new ArtPilot(this.sim, artLegs(art, this.sim.grid.width));
      const turn = this.artPilot.decide();
      if (turn) this.sim.enqueue({ type: 'turn', turn });
      return;
    }
    if (this.sim.level.soccer) {
      // Mower soccer: get behind the ball and kick it toward the nearer goal.
      this.soccerPilot ??= new SoccerPilot(this.sim);
      const turn = this.soccerPilot.decide();
      if (turn) this.sim.enqueue({ type: 'turn', turn });
      return;
    }
    if (this.sim.level.herd) {
      // Herding lawns: nudge the sheep into the pen one by one, then mow the rest.
      this.herdPilot ??= new HerdPilot(this.sim);
      const turn = this.herdPilot.decide();
      if (turn) this.sim.enqueue({ type: 'turn', turn });
      return;
    }
    if (this.sim.level.storm) {
      // Storm lawns: zig-zag ahead of the rain and make for the shed.
      this.stormPilot ??= new StormPilot(this.sim);
      const want = this.stormPilot.decide();
      if (want !== null) this.sim.enqueue({ type: 'face', dir: want });
      return;
    }
    this.planner ??= new GreedyPlanner(this.sim);
    const action = this.planner.nextAction();
    if (action && action !== 'straight') this.sim.enqueue({ type: 'turn', turn: action });
  }

  /**
   * A critter bumped (a crash, or a small one poofed): it counts against the Gentle challenge, and
   * the first squirrel or rabbit tagged gives the Paw Print Stripes, kept even if the lawn fails.
   */
  private bumpedCritter(critter: CritterKind): void {
    this.critterBumps++;
    let tagged = false;
    if (!this.levelData.practice) saves().update((s) => (tagged = tagCritter(s, critter)));
    if (tagged) {
      this.tagReveal = { kind: 'critterTag', critter, item: CRITTER_TAG_COSMETIC };
      this.hud()?.showNotice(`TAG! You got the ${critter}!`);
    }
  }

  /** Fires a boost from stock (each kind once per attempt; not in the daily lawn). */
  private useBoost(i: number): void {
    if (this.boxItems) return this.useItem('turbo');
    const id = this.boostIds[i];
    if (this.phase !== 'playing' || !id || this.sim.boostUsed(id)) return;
    if (!this.levelData.practice && boostStock(saves().save, id) <= 0) return;
    this.sim.enqueue({ type: 'boost', boost: id });
  }

  private useTool(slot: number): void {
    if (this.boxItems) return this.useItem('trim');
    if (this.phase === 'playing' && slot < this.toolCount) this.sim.enqueue({ type: 'tool', slot });
  }

  /**
   * Fires an item a box gave (docs/DAILY_ITEMS.md); the sim ignores it while it cannot fire, so a
   * tap then says why rather than seeming lost (owner, 2026-11-04).
   */
  private useItem(item: ItemId): void {
    if (this.phase !== 'playing' || this.sim.heldItem(item) <= 0) return;
    if (item === 'turbo' && this.sim.boostTicks('turbo') > 0) {
      this.hud()?.showNotice('TURBO IS STILL ON');
      return;
    }
    if (item === 'trim' && this.sim.trimWait > 0) {
      this.hud()?.showNotice('TRIMMER IS RESETTING');
      return;
    }
    this.sim.enqueue({ type: 'item', item });
  }

  /** How many buttons the tool bar holds (0: no bar). */
  private barSlots(): number {
    return this.boxItems ? 2 : this.toolCount + this.boostIds.length;
  }

  /** Switches between the normal and slow gear (mowers with a throttle only). */
  private readonly toggleThrottle = (): void => {
    if (!this.sim.mowerSpec.throttle || this.phase !== 'playing') return;
    this.sim.enqueue({ type: 'throttle', slow: !this.sim.mower.slow });
  };

  private readonly toggleAutopilot = (): void => {
    this.autopilot = !this.autopilot;
    if (this.autopilot) this.autopilotUsed = true;
  };

  private readonly toggleScheme = (): void => {
    this.scheme = this.scheme === 'swipe' ? 'tap' : 'swipe';
    this.controls.setScheme(this.scheme);
  };

  private updateHud(): void {
    const boss = this.stormLabel();
    // The clock, unless a boss puts its own count there.
    const clock = boss.label === undefined ? this.clock() : null;
    this.hud()?.setInfo({
      label: clock?.label ?? this.hudLabel(),
      ...(clock ? { timer: clock.timer } : {}),
      percent: this.sim.grid.percentMowed,
      ...boss,
      hp: this.sim.mower.hp,
      maxHp: this.sim.mowerSpec.maxHp,
      wallet: this.walletShown ?? saves().save.coins,
      // Campaign lawns pay out what you pick up; an endless run shows its bank plus this lawn.
      pending: this.levelData.endless
        ? this.runPending()
        : this.levelData.playlist?.kind === 'campaign' &&
            !this.levelData.daily &&
            this.phase !== 'ending' &&
            this.phase !== 'ended'
          ? this.sim.coins + this.sim.spareCoins + this.sim.treasure
          : null,
      fuel:
        this.sim.mowerSpec.fuelTiles === undefined
          ? null
          : this.sim.mower.fuel / this.sim.mowerSpec.fuelTiles,
      slow: this.sim.mowerSpec.throttle ? this.sim.mower.slow : null,
      tools: this.boxItems
        ? [
            {
              charges: this.sim.heldItem('trim'),
              ready: this.sim.heldItem('trim') > 0 && this.sim.trimWait === 0,
            },
          ]
        : this.sim.tools.map((_, i) => {
            const t = this.sim.toolState(i);
            return { charges: t?.charges ?? 0, ready: !!t && t.charges > 0 && t.cooldown === 0 };
          }),
      boosts: this.boxItems
        ? [
            {
              count: this.sim.heldItem('turbo'),
              usable: this.sim.heldItem('turbo') > 0 && this.sim.boostTicks('turbo') === 0,
              left: this.sim.boostTicks('turbo') / BOOST_TICKS.turbo,
            },
          ]
        : this.boostIds.map((id) => {
            const count = this.levelData.practice ? 1 : boostStock(saves().save, id);
            return {
              count,
              usable: count > 0 && !this.sim.boostUsed(id),
              left: this.sim.boostTicks(id) / BOOST_TICKS[id],
            };
          }),
      // (The store screenshots drive with the autopilot but show the HUD as a player sees it.)
      autopilot: this.autopilot && !storePreview(),
      flat: this.sim.mower.flat,
    });
  }

  /**
   * Which world's surroundings to draw: a builder lawn's own yard, campaign lawns by number,
   * generated ones by level.
   */
  private biome(): BiomeId {
    const d = this.levelData;
    if (d.playlist?.back && d.level.biome) return d.level.biome;
    const n = d.playlist?.kind === 'campaign' ? d.playlist.index + 1 : (d.level.level ?? 1);
    return biomeForLevel(n).id;
  }

  /** The Tuned Engine level in use on this mower: the par and gold goals are scaled to it. */
  private engine(): number {
    // (The Daily's loaner is a stock mower.)
    return this.levelData.daily ? 0 : engineInUse(saves().save, this.levelData.mowerId);
  }

  /**
   * The lawn's clock for the HUD (owner, 2026-10-23: see the time while mowing), gold while the
   * par star's time can still be beaten. Not in Endless, which counts lawns instead.
   */
  private clock(): { label: string; timer: 'ahead' | 'behind' | 'plain' } | null {
    if (this.levelData.endless) return null;
    const goal = parGoalTicks(this.levelData.level, this.levelData.mowerId, this.engine());
    const t = this.sim.tick;
    return {
      label: formatClock(t, TICK_RATE),
      timer: goal === null ? 'plain' : t <= goal ? 'ahead' : 'behind',
    };
  }

  private hudLabel(): string {
    if (this.levelData.daily) return 'DAILY';
    const run = this.levelData.endless;
    if (run?.mode === 'rain') return `ROW ${this.rowsClimbed()}`;
    if (run) return `RUN ${run.stage}`;
    const list = this.levelData.playlist;
    if (!list) return 'LAWN';
    if (list.featured) return 'WEEKLY';
    // A lawn of your own: its name.
    if (list.back) return this.levelData.level.name.toUpperCase();
    return `${list.kind === 'campaign' ? 'LAWN' : 'TEST'} ${this.lawnNumber()}`;
  }

  /** The lawn's number: its place in the campaign (a campaign lawn tested from DEV keeps it). */
  private lawnNumber(): number {
    const list = this.levelData.playlist!;
    return list.kind === 'test' ? (this.levelData.level.level ?? list.index + 1) : list.index + 1;
  }

  /**
   * Boss HUD label: the storm counts down to the rain reaching the mower (red when close), lawn art
   * shows the picture match, herding the sheep penned.
   */
  private stormLabel(): { label?: string; danger?: boolean } {
    const party = this.sim.partyEta();
    if (party !== null) {
      const seconds = Math.ceil(party / TICK_RATE);
      return { label: `PARTY ${seconds}s`, danger: seconds <= 10 };
    }
    const half = this.sim.halftimeEta();
    const soccer = this.sim.level.soccer;
    if (half !== null && soccer) {
      // Mower soccer: goals against the target, and the clock (red when short of goals, late on).
      const seconds = Math.ceil(half / TICK_RATE);
      const goals = this.sim.goals;
      return {
        label:
          goals < soccer.target
            ? `GOALS ${goals}/${soccer.target} ${seconds}s`
            : `GOALS ${goals}! ${seconds}s`,
        danger: seconds <= 10 && goals < soccer.target,
      };
    }
    const match = this.sim.artMatch();
    if (match !== null) return { label: `ART ${Math.round(match * 100)}%` };
    const sheep = this.sim.sheepCount();
    if (sheep) return { label: `SHEEP ${sheep.penned}/${sheep.total}` };
    const score = this.sim.rivalScore();
    if (score) return { label: `YOU ${score.you}-${score.robot}`, danger: score.you < score.robot };
    const eta = this.sim.stormEta();
    if (eta === null) return {};
    const seconds = Math.ceil(eta / TICK_RATE);
    // Rain Chase: how far up you are, red when the rain is close.
    if (this.levelData.endless?.mode === 'rain') {
      return { label: `ROW ${this.rowsClimbed()}`, danger: seconds <= 5 };
    }
    return { label: `RAIN ${seconds}s`, danger: seconds <= 5 };
  }

  /** The pause card's lawn line: "Lawn 26: Dewy Commons", the run's lawn, or the lawn's name. */
  private pauseLawnLine(): string {
    const run = this.levelData.endless;
    if (run) return `${run.mode === 'rain' ? 'Stage' : 'Lawn'} ${run.stage} of the run`;
    if (this.levelData.daily) return `Daily: ${this.levelData.level.name}`;
    const list = this.levelData.playlist;
    if (list?.kind === 'campaign' && !list.back) {
      return `Lawn ${this.lawnNumber()}: ${this.levelData.level.name}`;
    }
    return this.levelData.level.name;
  }

  private levelTitle(): string {
    const list = this.levelData.playlist;
    const name = this.levelData.level.name;
    return list && !this.levelData.practice ? `${this.lawnNumber()}. ${name}` : name;
  }

  /** How to steer and how to start, in a line. */
  private startHint(): string {
    return this.scheme === 'swipe'
      ? 'Swipe to steer. Tap to go.'
      : 'Tap a side to turn.\nTap to go.';
  }

  private hud(): HudScene | null {
    return this.scene.get('Hud') as HudScene | null;
  }

  /** The lawn camera sits below the HUD band. */
  private readonly layoutCamera = (): void => {
    const { width, height } = this.scale.gameSize;
    const toolbar = this.barSlots() > 0 ? TOOLBAR_HEIGHT : 0;
    this.cameras.main.setViewport(0, HUD_HEIGHT, width, height - HUD_HEIGHT - toolbar);
    this.cameras.main.setBackgroundColor(BACKGROUND);
  };
}
